import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Camera, Check, ExternalLink, Images, PackageCheck, RefreshCw, Send, Trash2 } from 'lucide-react';
import { api, errorMessage } from '../lib/api';
import { shrinkPhoto } from '../lib/photos';
import { cn, fmtDate } from '../lib/utils';

const MAX_PHOTOS = 10;
const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/* A parcel with no purchase order because it belongs to after-sales (a
   replacement, a part for a customer's claim). Photograph the paperwork and
   the label; Claude reads the claim's reference, Inny finds the sale in Odoo,
   and the photos go to #sav-warehouse. */
export default function ClaimForm() {
  const [photos, setPhotos] = useState([]); // { id, blob, url }
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(null); // the claim just sent, or one reopened from the list
  const [recent, setRecent] = useState(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const loadRecent = useCallback(async () => {
    try {
      setRecent((await api.claims(10)).claims);
    } catch {
      setRecent([]);
    }
  }, []);
  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // reading and posting run on the server: follow until done
  const pending = current?.status === 'pending';
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(async () => {
      try {
        const c = await api.claim(current.id);
        setCurrent(c);
        if (c.status !== 'pending') loadRecent();
      } catch {
        /* next tick */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [pending, current?.id, loadRecent]);

  const addFiles = async (files) => {
    const list = [...files].slice(0, Math.max(0, MAX_PHOTOS - photos.length));
    if (!list.length) return;
    setBusy(true);
    try {
      const prepared = [];
      for (const f of list) prepared.push({ id: `${Date.now()}-${Math.random()}`, ...(await shrinkPhoto(f)) });
      setPhotos((p) => [...p, ...prepared]);
    } catch (err) {
      setError(errorMessage(err, 'That photo could not be read'));
    } finally {
      setBusy(false);
    }
  };
  const onInput = (e) => {
    const files = [...(e.target.files || [])]; // copy before the reset empties the live list
    e.target.value = '';
    if (files.length) addFiles(files);
  };
  const remove = (id) =>
    setPhotos((p) => {
      const gone = p.find((x) => x.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return p.filter((x) => x.id !== id);
    });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      photos.forEach((p, i) => form.append('photos', p.blob, `photo-${i + 1}.jpg`));
      form.append('ref', ref.trim());
      form.append('note', note.trim());
      setCurrent(await api.createClaim(form));
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos([]);
      setRef('');
      setNote('');
      loadRecent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    try {
      setCurrent(await api.retryClaim(current.id));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (current) {
    return (
      <>
        <ClaimResult claim={current} onRetry={retry} onNew={() => { setCurrent(null); setError(null); }} />
        {error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
      </>
    );
  }

  return (
    <>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onInput} />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onInput} />

      <p className="mt-3 px-1 text-[13px] text-ink2">A parcel without a PO that belongs to after-sales? Photograph the paperwork and the label. Inny finds the claim and posts it in #sav-warehouse.</p>

      <section className="mt-2 rounded-xl bg-surface p-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[13px] font-semibold text-ink2">1 · Photos</h2>
          <span className="text-[12px] text-ink2">{photos.length ? `${photos.length} of ${MAX_PHOTOS}` : 'paperwork, label, the item'}</span>
        </div>
        {photos.length > 0 && (
          <ul className="-mx-1 mt-2 flex gap-2 overflow-x-auto px-1 pb-1">
            {photos.map((p, i) => (
              <li key={p.id} className="relative shrink-0">
                <img src={p.url} alt={`Photo ${i + 1}`} className="h-28 w-20 rounded-lg object-cover" />
                <button type="button" onClick={() => remove(p.id)} aria-label={`Remove photo ${i + 1}`} className="absolute -right-1.5 -top-1.5 flex h-9 w-9 items-center justify-center rounded-full bg-alert text-bg">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={() => cameraRef.current?.click()} disabled={busy || photos.length >= MAX_PHOTOS} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-ink text-[15px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
            <Camera className="h-5 w-5" /> {photos.length ? 'Another photo' : 'Take a photo'}
          </button>
          <button type="button" onClick={() => galleryRef.current?.click()} disabled={busy || photos.length >= MAX_PHOTOS} aria-label="Add from the gallery" className="flex h-12 w-12 items-center justify-center rounded-xl bg-sunken active:bg-line disabled:opacity-40">
            <Images className="h-5 w-5" />
          </button>
        </div>
      </section>

      <section className="mt-3 rounded-xl bg-surface p-3">
        <h2 className="text-[13px] font-semibold text-ink2">2 · Reference (optional)</h2>
        <p className="mt-0.5 text-[12px] text-ink2">Only if you see it. Inny reads it from the photos anyway.</p>
        <input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={60} placeholder="185055/LT" autoCapitalize="characters" className="mt-2 h-11 w-full rounded-xl border border-line bg-surface px-3 text-[15px] outline-none focus:border-ink" />
        <h2 className="mt-3 text-[13px] font-semibold text-ink2">3 · Note (optional)</h2>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Box damaged, 2 parcels…" className="mt-2 h-11 w-full rounded-xl border border-line bg-surface px-3 text-[15px] outline-none focus:border-ink" />
      </section>

      {error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
      <button type="button" onClick={submit} disabled={busy || !photos.length} className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[17px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
        <Send className="h-5 w-5" /> {busy ? 'Sending…' : !photos.length ? 'Take the photos first' : 'Send to #sav-warehouse'}
      </button>

      {recent && recent.length > 0 && (
        <section className="mt-5">
          <h2 className="mb-1.5 px-1 text-[13px] font-semibold text-ink2">Recent claims</h2>
          <ul className="space-y-1.5">
            {recent.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => setCurrent(c)} className="w-full rounded-xl bg-surface px-3 py-2 text-left active:bg-sunken">
                  <span className="font-cond block truncate text-[18px] font-bold leading-none">{headline(c)}</span>
                  <span className="mt-1 block truncate text-[12px] text-ink2">
                    {fmtDate(c.created_at)} {timeOf(c.created_at)}{c.user && ` · ${c.user}`}{c.supplier && ` · ${c.supplier}`}
                  </span>
                  <StatusPill claim={c} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function headline(c) {
  if (c.sales?.length) return c.sales[0].name;
  if (c.orders?.length) return c.orders[0].name;
  const ours = (c.references || []).find((r) => r.kind === 'our_sale');
  if (ours) return ours.value;
  if (c.typed_ref) return c.typed_ref;
  return c.status === 'pending' ? 'Reading…' : 'No reference found';
}

function StatusPill({ claim }) {
  const c = claim;
  return (
    <span className={cn('mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold', c.status === 'done' ? 'tone-out bg-tone-bg text-tone' : c.status === 'failed' ? 'bg-alert-bg text-alert' : 'bg-sunken text-ink2')}>
      {c.status === 'done' ? 'Posted in #sav-warehouse' : c.status === 'failed' ? 'Not posted · open to retry' : 'Reading and posting…'}
    </span>
  );
}

function ClaimResult({ claim: c, onRetry, onNew }) {
  const navigate = useNavigate();
  const others = (c.references || []).filter((r) => r.kind !== 'our_sale');
  const ours = (c.references || []).filter((r) => r.kind === 'our_sale');
  return (
    <section className="mt-3 rounded-xl bg-surface p-4">
      <div className={cn('flex items-center gap-3 rounded-2xl px-4 py-4', c.status === 'done' ? 'tone-out bg-tone-bg' : c.status === 'failed' ? 'bg-alert-bg' : 'bg-sunken')}>
        {c.status === 'pending' ? <RefreshCw className="h-8 w-8 animate-spin text-ink2" /> : c.status === 'done' ? <Check className="pop h-8 w-8 text-tone" /> : <AlertTriangle className="h-8 w-8 text-alert" />}
        <div className="min-w-0">
          <p className={cn('font-cond text-[24px] font-bold leading-none', c.status === 'done' ? 'text-tone' : c.status === 'failed' ? 'text-alert' : '')}>
            {c.status === 'pending' ? 'Reading the photos…' : c.status === 'done' ? 'Posted in #sav-warehouse' : 'Not posted yet'}
          </p>
          <p className="mt-1 text-[14px] text-ink2">{c.status === 'pending' ? 'Takes about half a minute. You can carry on.' : c.status === 'done' ? 'After-sales will tell you where it goes.' : 'The photos are saved. Try again.'}</p>
        </div>
      </div>

      {c.status !== 'pending' && (
        <dl className="mt-3 space-y-2 text-[14px]">
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink2">Sale in Odoo</dt>
            <dd>
              {c.sales?.length ? (
                <ul className="mt-1 flex flex-wrap gap-2">
                  {c.sales.map((s) => (
                    <li key={s.id}>
                      <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-sunken px-3 font-semibold active:bg-line">
                        {s.name} {s.customer && <span className="font-normal text-ink2">{s.customer}</span>} <ExternalLink className="h-4 w-4" />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="font-semibold">{ours.length ? `${ours.map((r) => r.value).join(', ')}: not found in Odoo` : 'No sale reference on the paperwork'}</span>
              )}
            </dd>
          </div>
          {c.orders?.length > 0 && (
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink2">Claim PO</dt>
              <dd>
                <ul className="mt-1 space-y-1.5">
                  {c.orders.map((o) => (
                    <li key={o.id} className="flex items-center gap-2 rounded-xl bg-sunken px-3 py-2">
                      <span className="min-w-0 flex-1">
                        <span className="font-cond block truncate text-[17px] font-bold leading-tight">{o.name}</span>
                        <span className="block truncate text-[12px] text-ink2">{[o.supplier, o.partner_ref && `vendor ref ${o.partner_ref}`, o.open ? 'to receive' : 'already received'].filter(Boolean).join(' · ')}</span>
                      </span>
                      {o.open ? (
                        <button type="button" onClick={() => navigate(`/receive/${o.id}`)} className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl bg-ink px-3 text-[14px] font-semibold text-bg active:opacity-80">
                          <PackageCheck className="h-4 w-4" /> Receive
                        </button>
                      ) : (
                        <a href={o.url} target="_blank" rel="noreferrer" aria-label={`${o.name} in Odoo`} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl active:bg-line">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          )}
          {c.supplier && (
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink2">Supplier</dt>
              <dd className="font-semibold">{c.supplier}</dd>
            </div>
          )}
          {c.products?.length > 0 && (
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink2">In the parcel</dt>
              <dd>
                <ul>
                  {c.products.map((p, i) => (
                    <li key={i}>{p.quantity != null && <span className="font-semibold">{p.quantity}× </span>}{p.description}{p.code && <span className="font-mono text-[12px] text-ink2"> {p.code}</span>}</li>
                  ))}
                </ul>
              </dd>
            </div>
          )}
          {others.length > 0 && (
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink2">Other references</dt>
              <dd className="text-ink2">{others.map((r) => r.value).join(' · ')}</dd>
            </div>
          )}
          {c.error && <p className="rounded-xl bg-alert-bg px-3 py-2 text-[13px] text-alert">{c.error}</p>}
        </dl>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {c.slack_link && (
          <a href={c.slack_link} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-sunken px-3 text-[14px] font-semibold active:bg-line">
            Open in Slack <ExternalLink className="h-4 w-4" />
          </a>
        )}
        {c.status === 'failed' && (
          <button type="button" onClick={onRetry} className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-sunken px-3 text-[14px] font-semibold active:bg-line">
            <RefreshCw className="h-4 w-4" /> Try again
          </button>
        )}
      </div>
      <button type="button" onClick={onNew} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[16px] font-semibold text-bg active:opacity-80">
        <Camera className="h-5 w-5" /> New claim
      </button>
    </section>
  );
}
